import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import apiService from '../../services/api';

// Async thunks
export const fetchSubscriptions = createAsyncThunk(
  'subscriptions/fetchSubscriptions',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiService.getSubscriptions();
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to fetch subscriptions');
    }
  }
);

export const fetchSubscriptionPlans = createAsyncThunk(
  'subscriptions/fetchSubscriptionPlans',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiService.getSubscriptionPlans();
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to fetch subscription plans');
    }
  }
);

export const fetchSubscriptionDetails = createAsyncThunk(
  'subscriptions/fetchSubscriptionDetails',
  async (id, { rejectWithValue }) => {
    try {
      const response = await apiService.getSubscriptionDetails(id);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to fetch subscription details');
    }
  }
);

export const createDirectSubscription = createAsyncThunk(
  'subscriptions/createDirectSubscription',
  async (subscriptionData, { rejectWithValue }) => {
    try {
      const response = await apiService.createPaymentIntent(subscriptionData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to create subscription');
    }
  }
);

export const completeDirectSubscription = createAsyncThunk(
  'subscriptions/completeDirectSubscription',
  async ({ paymentMethodId, setupFutureUsage, clientSecret }, { rejectWithValue }) => {
    try {
      // Extract payment intent ID from client secret
      const paymentIntentId = clientSecret.split('_secret_')[0];
      
      const response = await apiService.completeSubscription({
        payment_intent_id: paymentIntentId,
        payment_method_id: paymentMethodId,
        setup_future_usage: setupFutureUsage
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to complete subscription');
    }
  }
);

export const updateSubscription = createAsyncThunk(
  'subscriptions/updateSubscription',
  async ({ id, ...updates }, { rejectWithValue }) => {
    try {
      const response = await apiService.updateSubscription(id, updates);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to update subscription');
    }
  }
);

export const cancelSubscription = createAsyncThunk(
  'subscriptions/cancelSubscription',
  async (id, { rejectWithValue }) => {
    try {
      const response = await apiService.cancelSubscription(id);
      return { id, ...response.data };
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to cancel subscription');
    }
  }
);

export const pauseSubscription = createAsyncThunk(
  'subscriptions/pauseSubscription',
  async (id, { rejectWithValue }) => {
    try {
      const response = await apiService.pauseSubscription(id);
      return { id, ...response.data };
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to pause subscription');
    }
  }
);

export const resumeSubscription = createAsyncThunk(
  'subscriptions/resumeSubscription',
  async (id, { rejectWithValue }) => {
    try {
      const response = await apiService.resumeSubscription(id);
      return { id, ...response.data };
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to resume subscription');
    }
  }
);

export const fetchSubscriptionBalance = createAsyncThunk(
  'subscriptions/fetchSubscriptionBalance',
  async (id, { rejectWithValue }) => {
    try {
      const response = await apiService.getSubscriptionBalance(id);
      return { id, balance: response.data };
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to fetch subscription balance');
    }
  }
);

export const fetchPaymentMethods = createAsyncThunk(
  'subscriptions/fetchPaymentMethods',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiService.getPaymentMethods();
      // Ensure we're handling the response structure correctly
      return response.data.payment_methods || [];
    } catch (error) {
      console.error('Payment methods fetch error:', error);
      // Return empty array instead of error object to prevent rendering issues
      return [];
    }
  }
);

// Subscription slice
const subscriptionSlice = createSlice({
  name: 'subscriptions',
  initialState: {
    subscriptions: [],
    plans: [],
    selectedSubscription: null,
    paymentIntent: null,
    paymentMethods: [],
    subscriptionCreated: false,
    loading: false,
    error: null,
  },
  reducers: {
    selectSubscription: (state, action) => {
      state.selectedSubscription = action.payload;
    },
    clearSelectedSubscription: (state) => {
      state.selectedSubscription = null;
    },
    resetSubscriptionCreated: (state) => {
      state.subscriptionCreated = false;
      state.paymentIntent = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // fetchSubscriptions
      .addCase(fetchSubscriptions.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSubscriptions.fulfilled, (state, action) => {
        state.loading = false;
        state.subscriptions = action.payload;
      })
      .addCase(fetchSubscriptions.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // fetchSubscriptionPlans
      .addCase(fetchSubscriptionPlans.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSubscriptionPlans.fulfilled, (state, action) => {
        state.loading = false;
        state.plans = action.payload;
      })
      .addCase(fetchSubscriptionPlans.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // fetchSubscriptionDetails
      .addCase(fetchSubscriptionDetails.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSubscriptionDetails.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedSubscription = action.payload;
      })
      .addCase(fetchSubscriptionDetails.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // createDirectSubscription
      .addCase(createDirectSubscription.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.subscriptionCreated = false;
      })
      .addCase(createDirectSubscription.fulfilled, (state, action) => {
        state.loading = false;
        state.paymentIntent = action.payload;
      })
      .addCase(createDirectSubscription.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // completeDirectSubscription
      .addCase(completeDirectSubscription.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(completeDirectSubscription.fulfilled, (state, action) => {
        state.loading = false;
        state.subscriptionCreated = true;
        state.subscriptions.push(action.payload.subscription);
      })
      .addCase(completeDirectSubscription.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // updateSubscription
      .addCase(updateSubscription.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateSubscription.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.subscriptions.findIndex(sub => sub.id === action.payload.id);
        if (index !== -1) {
          state.subscriptions[index] = action.payload;
        }
        if (state.selectedSubscription && state.selectedSubscription.id === action.payload.id) {
          state.selectedSubscription = action.payload;
        }
      })
      .addCase(updateSubscription.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // cancelSubscription
      .addCase(cancelSubscription.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(cancelSubscription.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.subscriptions.findIndex(sub => sub.id === action.payload.id);
        if (index !== -1) {
          state.subscriptions[index].status = 'cancelled';
        }
        if (state.selectedSubscription && state.selectedSubscription.id === action.payload.id) {
          state.selectedSubscription.status = 'cancelled';
        }
      })
      .addCase(cancelSubscription.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // pauseSubscription
      .addCase(pauseSubscription.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(pauseSubscription.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.subscriptions.findIndex(sub => sub.id === action.payload.id);
        if (index !== -1) {
          state.subscriptions[index].status = 'paused';
        }
        if (state.selectedSubscription && state.selectedSubscription.id === action.payload.id) {
          state.selectedSubscription.status = 'paused';
        }
      })
      .addCase(pauseSubscription.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // resumeSubscription
      .addCase(resumeSubscription.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(resumeSubscription.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.subscriptions.findIndex(sub => sub.id === action.payload.id);
        if (index !== -1) {
          state.subscriptions[index].status = 'active';
        }
        if (state.selectedSubscription && state.selectedSubscription.id === action.payload.id) {
          state.selectedSubscription.status = 'active';
        }
      })
      .addCase(resumeSubscription.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // fetchSubscriptionBalance
      .addCase(fetchSubscriptionBalance.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSubscriptionBalance.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.subscriptions.findIndex(sub => sub.id === action.payload.id);
        if (index !== -1) {
          state.subscriptions[index].balance = action.payload.balance;
        }
        if (state.selectedSubscription && state.selectedSubscription.id === action.payload.id) {
          state.selectedSubscription.balance = action.payload.balance;
        }
      })
      .addCase(fetchSubscriptionBalance.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // fetchPaymentMethods
      .addCase(fetchPaymentMethods.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPaymentMethods.fulfilled, (state, action) => {
        state.loading = false;
        state.paymentMethods = action.payload;
      })
      .addCase(fetchPaymentMethods.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { selectSubscription, clearSelectedSubscription, resetSubscriptionCreated } = subscriptionSlice.actions;
export default subscriptionSlice.reducer;
